import React, { createContext, useContext, useState, useEffect } from 'react';
import { useMaintenanceService } from '../services/maintenanceService';
import { AuthContext } from './AuthContext';

const MaintenanceContext = createContext();

export const useMaintenanceContext = () => {
  const context = useContext(MaintenanceContext);
  return context;
};

export const MaintenanceProvider = ({ children }) => {
  const { user } = useContext(AuthContext);
  const { updateMaintenanceMode } = useMaintenanceService();
  const [maintenanceStatus, setMaintenanceStatus] = useState({
    isEnabled: false,
    message: 'System is operational'
  });
  const [isLoading, setIsLoading] = useState(false);
  const [shouldBlockUser, setShouldBlockUser] = useState(false);

  // Load maintenance status from user settings (already fetched)
  useEffect(() => {
    if (user?.maintenanceStatus) {
      setMaintenanceStatus(user.maintenanceStatus);
    } else if (user) {
      setMaintenanceStatus({ isEnabled: false, message: 'System is operational' });
    }
  }, [user?.maintenanceStatus]);

  // Check if user should be blocked (maintenance enabled and not superuser)
  useEffect(() => {
    // Only check blocking after we have both maintenance status and user info
    if (maintenanceStatus.isEnabled && user) {
      if (!user.superuser) {
        setShouldBlockUser(true);
      } else {
        setShouldBlockUser(false);
      }
    } else {
      setShouldBlockUser(false);
    }
  }, [maintenanceStatus.isEnabled, user]);

  // Legacy function for compatibility (no longer makes API call on load)
  const loadMaintenanceStatus = async () => {
    // Data is already loaded from settings, so just return current state
    return maintenanceStatus;
  };

  const enableMaintenanceMode = async (message = null) => {
    setIsLoading(true);
    try {
      const response = await updateMaintenanceMode(true, message);
      setMaintenanceStatus(response);
      return response;
    } catch (error) {
      console.error('Error enabling maintenance mode:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const disableMaintenanceMode = async () => {
    setIsLoading(true);
    try {
      const response = await updateMaintenanceMode(false);
      setMaintenanceStatus(response);
      return response;
    } catch (error) {
      console.error('Error disabling maintenance mode:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const value = {
    maintenanceStatus,
    isLoading,
    enableMaintenanceMode,
    disableMaintenanceMode,
    loadMaintenanceStatus,
    setMaintenanceStatus,
    shouldBlockUser
  };

  return (
    <MaintenanceContext.Provider value={value}>
      {children}
    </MaintenanceContext.Provider>
  );
}; 